import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const inputCls = "w-full border border-line bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold";
const labelCls = "block font-mono text-[10px] tracking-[0.18em] uppercase text-slate2 mb-1.5";

export default function SettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({ ...r.data, areasText: (r.data.service_areas || []).join("\n") })).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        phone: form.phone.trim(),
        email: form.email.trim(),
        facebook: (form.facebook || "").trim(),
        instagram: (form.instagram || "").trim(),
        service_areas: form.areasText.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Settings saved — the website is updated");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!form) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div data-testid="settings-form" className="bg-white border border-line p-5 md:p-7 max-w-2xl">
      <h2 className="font-display font-bold text-xl text-ink mb-1">Website & Contact Settings</h2>
      <p className="text-xs text-slate2 mb-6">Changes here update the whole website — header, contact section, footer and the mobile Call button.</p>
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Phone Number</label>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="settings-phone-input" className={inputCls} placeholder="+64 21 000 0000" />
        </div>
        <div>
          <label className={labelCls}>Contact Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="settings-email-input" className={inputCls} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Facebook URL (optional)</label>
            <input value={form.facebook || ""} onChange={(e) => setForm({ ...form, facebook: e.target.value })} data-testid="settings-facebook-input" className={inputCls} placeholder="https://facebook.com/…" />
          </div>
          <div>
            <label className={labelCls}>Instagram URL (optional)</label>
            <input value={form.instagram || ""} onChange={(e) => setForm({ ...form, instagram: e.target.value })} data-testid="settings-instagram-input" className={inputCls} placeholder="https://instagram.com/…" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Service Areas (one per line)</label>
          <textarea rows={6} value={form.areasText} onChange={(e) => setForm({ ...form, areasText: e.target.value })} data-testid="settings-areas-input" className={`${inputCls} resize-y`} />
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="mt-6 inline-flex items-center gap-2 bg-gold hover:bg-golddark text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3 disabled:opacity-60 transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
      </button>
    </div>
  );
}
